<script setup>
import { useI18n } from 'vue-i18n';
import { onMounted, onUnmounted, ref } from 'vue';
import { animate, set } from 'animejs';
import axios from 'axios';
import LogoCard from '@/components/Cards/LogoCard.vue';

const { t } = useI18n({ useScope: 'global' });
const defaultError = t('problem');
const schemeNames = {
  loadStatus: 0,
  data: [],
  error: {
    code: 0,
    message: t('problem'),
  },
};
const source = import.meta.env.VITE_BASE_API ?? 'http://127.0.0.1:10600';

const queueArray = ref([]);
const slideContainer = ref(null);

const vsOpts = {
  idleMs: 2500,
  duration: 900,
  lineHeight: 2 * 16, // px — must match .slider-item line-height
};

let currentIndex = 0;
let idleTimeout = null;

const getNextIndex = (index) => (index + 1) % schemeNames.data.length;

const updateVisibleSlides = () => {
  const prevIndex = currentIndex === 0 ? schemeNames.data.length - 1 : currentIndex - 1;
  const nextIndex = getNextIndex(currentIndex);
  queueArray.value[0] = schemeNames.data[prevIndex];  // above frame
  queueArray.value[1] = schemeNames.data[currentIndex]; // visible (center)
  queueArray.value[2] = schemeNames.data[nextIndex];   // below frame
  currentIndex = nextIndex;
};

const tick = () => {
  // Scroll container up one slot with elastic overshoot:
  // — outgoing text exits the top while incoming text overshoots upward then springs back to center
  animate(slideContainer.value, {
    translateY: -vsOpts.lineHeight,
    ease: 'easeOutElastic(1, .5)',
    duration: vsOpts.duration,
    onComplete: () => {
      updateVisibleSlides();
      set(slideContainer.value, { translateY: 0 });
      idleTimeout = setTimeout(tick, vsOpts.idleMs);
    },
  });
};

const getSchemeNames = async () => {
  schemeNames.loadStatus = 0;
  await axios
    .get(`${source}/api/pub/scheme`)
    .then((res) => {
      schemeNames.loadStatus = 1;
      schemeNames.data = res.data.content;
    })
    .catch((err) => {
      schemeNames.loadStatus = -1;
      schemeNames.error.code = err.response?.status;
      schemeNames.error.message = err.response?.data?.error ?? defaultError;
    });
};

const mountNames = () => {
  getSchemeNames().then(() => {
    if (!schemeNames.data.length) return;
    queueArray.value = [
      schemeNames.data[schemeNames.data.length - 1],
      schemeNames.data[0],
      schemeNames.data[1] ?? schemeNames.data[0],
    ];
    set(slideContainer.value, { translateY: 0 });
    idleTimeout = setTimeout(tick, vsOpts.idleMs);
  });
};

onMounted(() => {
  mountNames();
});

onUnmounted(() => {
  clearTimeout(idleTimeout);
});
</script>

<template>
  <div class="text-center">
    <div class="flex justify-center mb-4">
      <LogoCard />
    </div>
    <h1>{{ t('title') }}</h1>
    <div class="overflow-hidden slider-frame">
      <ul ref="slideContainer" class="slider-container">
        <li v-for="(slide, index) in queueArray" :key="index" class="slider-item text-white">
          {{ slide }}
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.slider-frame {
  height: 2rem;
  overflow: hidden;
  text-align: center;
}
ul.slider-container {
  list-style-type: none;
  padding: 0;
  margin: 0;
}
.slider-item {
  font-size: 1.2rem;
  line-height: 2rem;
}
</style>
